import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, Printer, Receipt } from 'lucide-react';
import { Button } from '@/components/common/Button';
import { Card } from '@/components/common/Card';
import { EmptyState, ErrorState, LoadingState } from '@/components/common/States';
import { ROUTES } from '@/app/router/routes';
import { ReceiptDocument } from '@/features/finance/ReceiptPage';
import { portalApi } from '@/services/api/endpoints/portal';
import { queryKeys } from '@/services/api/queryKeys';
import { PortalShell, type PortalContext } from './PortalShell';
import { isNotFound } from './shared';

/** A payment receipt for the family, rendered by the same component as the office's. */
export function PortalReceiptPage() {
  return (
    <PortalShell title="Receipt" icon={<Receipt className="h-5 w-5" aria-hidden="true" />}>
      {(context) => <ReceiptView context={context} />}
    </PortalShell>
  );
}

function ReceiptView({ context }: { context: PortalContext }) {
  const { subject, subjectKey, withChild } = context;
  const { paymentId = '' } = useParams();

  const receipt = useQuery({
    queryKey: queryKeys.portal.receipt(subjectKey, paymentId),
    queryFn: () => portalApi.receipt(subject, paymentId),
    enabled: paymentId !== '',
    retry: false,
  });

  const back = (
    <Link
      to={withChild(ROUTES.myFees)}
      className="inline-flex items-center gap-1.5 text-[13px] text-muted transition-colors hover:text-ink"
    >
      <ArrowLeft className="h-3.5 w-3.5" aria-hidden="true" />
      Back to fees
    </Link>
  );

  if (receipt.isPending) {
    return (
      <Card>
        <LoadingState label="Preparing the receipt" />
      </Card>
    );
  }

  if (receipt.isError) {
    return (
      <div>
        <div className="mb-3">{back}</div>
        <Card>
          {isNotFound(receipt.error) ? (
            <EmptyState title="Receipt not found" description="This payment is not on this student's account." />
          ) : (
            <ErrorState error={receipt.error} onRetry={() => void receipt.refetch()} />
          )}
        </Card>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 print:hidden">
        {back}
        <Button onClick={() => window.print()}>
          <Printer className="h-4 w-4" aria-hidden="true" />
          Print or save as PDF
        </Button>
      </div>
      <ReceiptDocument receipt={receipt.data} />
    </div>
  );
}
